import { sampleTypeLabel, type AssayRunConfig, type AssayType } from '@/types';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import Table from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import type { ColDef } from 'ag-grid-enterprise';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { deriveControlRows, type ControlRow } from './controlRows';

type Props = {
  /** Run config for the selected assay (POR-322 `/assays/run_configs`). `undefined` while assays
   *  load or when none is selected, which falls back to the hardcoded Tau layout via `assayType`. */
  assayRunConfig: AssayRunConfig | undefined;
  /** The selected CATALOG assay's type — only the fallback source when `assayRunConfig` is
   *  undefined. Not a form field. */
  assayType: AssayType | undefined;
  numSamples: number;
  libraryGroupCount: number;
  ownerOrgName: string;
  /** The run's study, which controls carry just like customer rows — the table has to show the
   *  same study the payload sends, or the two disagree about what is on the plate. */
  runStudyName: string;
  /** Actual flowcell SNs from run.consumables.three_flow_cell_ids (split by comma).
   *  When provided, replaces the "Flowcell N" label with the actual SN. */
  flowcellSns?: string[];
};

const ControlsSection = ({
  assayRunConfig,
  assayType,
  numSamples,
  libraryGroupCount,
  ownerOrgName,
  runStudyName,
  flowcellSns,
}: Props) => {
  const { t } = useTranslation('runs', { keyPrefix: 'controls' });

  const rowData = useMemo(
    () =>
      deriveControlRows(assayRunConfig, assayType, numSamples, libraryGroupCount, {
        orgName: ownerOrgName,
        studyName: runStudyName,
      }),
    [assayRunConfig, assayType, numSamples, libraryGroupCount, ownerOrgName, runStudyName],
  );

  const colDefs = useMemo<ColDef<ControlRow>[]>(
    () => [
      {
        field: 'flow_cell',
        headerName: t('flowcell'),
        editable: false,
        valueFormatter: params => {
          const fcNum = params.value as number | undefined;
          if (fcNum === undefined || fcNum === null) return '—';
          if (flowcellSns && flowcellSns[fcNum - 1]) return flowcellSns[fcNum - 1];
          return `Flowcell ${fcNum}`;
        },
        width: 120,
      },
      {
        field: 'lane',
        headerName: t('lane'),
        editable: false,
        width: 70,
      },
      {
        field: 'well_position',
        headerName: t('well'),
        editable: false,
        width: 70,
      },
      {
        field: 'demux_index',
        headerName: t('demux_id'),
        editable: false,
        width: 90,
      },
      {
        field: 'name',
        headerName: t('sample_name'),
        editable: false,
        flex: 1,
      },
      {
        field: 'study_name',
        headerName: t('study'),
        editable: false,
        flex: 1,
      },
      {
        field: 'org_name',
        headerName: t('organization'),
        editable: false,
        flex: 1,
      },
      {
        field: 'library_group',
        headerName: t('library_group'),
        editable: false,
        flex: 1,
      },
      {
        field: 'sample_group',
        headerName: t('sample_group'),
        editable: false,
        flex: 1,
      },
      {
        field: 'sample_type',
        headerName: t('type'),
        editable: false,
        flex: 1,
        valueFormatter: ({ value }) => (value ? sampleTypeLabel(value) : ''),
      },
    ],
    [t, flowcellSns],
  );

  if (rowData.length === 0) return null;

  return (
    <Stack fullWidth gap={2} orientation="vertical">
      <Text as="h3">{t('title')}</Text>
      <Table
        columnDefs={colDefs}
        domLayout="autoHeight"
        enableAdvancedFilter={false}
        rowData={rowData}
        suppressContextMenu
        tableId="controlsSection"
        enableResponsiveResize={false}
      />
    </Stack>
  );
};

export default memo(ControlsSection);
